/**
 * Admin Role Selector Component
 * Allows Central Admin to preview the UI as different roles
 * 
 * Features:
 * - Dropdown to select any role
 * - Clear "Preview Mode" indicator
 * - Quick exit from preview mode
 * - Full access mode (allows data mutations for testing)
 */
import React, { useState } from 'react';
import { Eye, EyeOff, ChevronDown, AlertTriangle, Check, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { ROLES, ROLE_DISPLAY_NAMES, ROLE_DESCRIPTIONS, ROLE_LEVELS } from '@/config/rbac';
import { cn } from '@/lib/utils';

// Role icons for visual distinction
const ROLE_ICONS = {
  [ROLES.BRAND_OWNER]: '👑',
  [ROLES.CENTRAL_ADMIN]: '⚙️',
  [ROLES.FRANCHISE_OWNER]: '🏢',
  [ROLES.REGIONAL_MANAGER]: '📍',
  [ROLES.RESTAURANT_MANAGER]: '🏪',
  [ROLES.CHEF]: '👨‍🍳',
  [ROLES.FOH_STAFF]: '🍽️',
  [ROLES.SUPPLIER]: '📦',
  [ROLES.CUSTOMER]: '👤',
};

// Role badge colors
const ROLE_COLORS = {
  [ROLES.BRAND_OWNER]: 'bg-purple-100 text-purple-800 border-purple-300',
  [ROLES.CENTRAL_ADMIN]: 'bg-blue-100 text-blue-800 border-blue-300',
  [ROLES.FRANCHISE_OWNER]: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  [ROLES.REGIONAL_MANAGER]: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  [ROLES.RESTAURANT_MANAGER]: 'bg-green-100 text-green-800 border-green-300',
  [ROLES.CHEF]: 'bg-orange-100 text-orange-800 border-orange-300',
  [ROLES.FOH_STAFF]: 'bg-amber-100 text-amber-800 border-amber-300',
  [ROLES.SUPPLIER]: 'bg-gray-100 text-gray-800 border-gray-300',
  [ROLES.CUSTOMER]: 'bg-pink-100 text-pink-800 border-pink-300',
};

export function RoleSelector() {
  const { 
    canUseRolePreview, 
    isPreviewMode, 
    previewRole,
    actualRole,
    effectiveRole,
    enableRolePreview, 
    disableRolePreview,
    roleDisplayName,
  } = useAuth();
  
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Only render for admins
  if (!canUseRolePreview) {
    return null;
  }
  
  // Get all roles sorted by level
  const sortedRoles = Object.values(ROLES).sort((a, b) => 
    (ROLE_LEVELS[a] || 99) - (ROLE_LEVELS[b] || 99)
  );
  
  const handleRoleSelect = (role) => {
    if (role === actualRole) {
      // Selecting own role exits preview mode
      disableRolePreview();
      return;
    }
    setSelectedRole(role);
    setConfirmDialogOpen(true);
  };
  
  const confirmRolePreview = () => {
    if (selectedRole) {
      enableRolePreview(selectedRole);
    }
    setConfirmDialogOpen(false);
    setSelectedRole(null);
  };
  
  return (
    <>
      <div className="flex items-center gap-2">
        {/* Preview Mode Indicator */}
        {isPreviewMode && (
          <Badge 
            variant="outline" 
            className="bg-amber-50 border-amber-300 text-amber-700 gap-1 animate-pulse"
          >
            <Eye className="h-3 w-3" />
            Preview Mode
          </Badge>
        )}
        
        {/* Role Selector Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant={isPreviewMode ? "outline" : "ghost"} 
              size="sm"
              className={cn(
                "gap-2",
                isPreviewMode && "border-amber-300 text-amber-700 hover:bg-amber-50"
              )}
              data-testid="role-selector-trigger"
            >
              <span className="text-lg">{ROLE_ICONS[effectiveRole] || '👤'}</span>
              <span className="hidden sm:inline text-sm">
                {isPreviewMode ? 'Viewing as' : 'Role Preview'}
              </span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Role Preview Mode
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Current actual role indicator */}
            <div className="px-2 py-2 bg-gray-50 rounded mx-2 mb-2">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                Your Actual Role
              </div>
              <div className="flex items-center gap-2">
                <span>{ROLE_ICONS[actualRole]}</span>
                <span className="text-sm font-medium">{ROLE_DISPLAY_NAMES[actualRole]}</span>
              </div>
            </div>
            
            <DropdownMenuLabel className="text-xs text-gray-500 font-normal">
              Select a role to preview
            </DropdownMenuLabel>
            
            {sortedRoles.map((role) => (
              <DropdownMenuItem
                key={role}
                onClick={() => handleRoleSelect(role)}
                className={cn(
                  "flex items-center gap-3 cursor-pointer",
                  effectiveRole === role && "bg-amber-50"
                )}
                data-testid={`role-option-${role}`}
              >
                <span className="text-lg w-6 text-center">{ROLE_ICONS[role]}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {ROLE_DISPLAY_NAMES[role]}
                    </span>
                    {role === actualRole && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        You
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {ROLE_DESCRIPTIONS[role]}
                  </p>
                </div>
                {effectiveRole === role && (
                  <Check className="h-4 w-4 text-amber-600" />
                )}
              </DropdownMenuItem>
            ))}
            
            {/* Exit Preview Button */}
            {isPreviewMode && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={disableRolePreview}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  data-testid="exit-preview-mode"
                >
                  <EyeOff className="h-4 w-4 mr-2" />
                  Exit Preview Mode
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              Enable Role Preview
            </DialogTitle>
            <DialogDescription>
              You are about to preview the application as a different role.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Selected Role Card */}
            <div className={cn(
              "p-4 rounded-lg border-2",
              ROLE_COLORS[selectedRole]
            )}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">{ROLE_ICONS[selectedRole]}</span>
                <div>
                  <div className="font-semibold">
                    {ROLE_DISPLAY_NAMES[selectedRole]}
                  </div>
                  <p className="text-sm opacity-80">
                    {ROLE_DESCRIPTIONS[selectedRole]}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Warning Notice */}
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Full Access Mode</p>
                  <p className="mt-1 text-amber-700">
                    This preview allows full data access including create, edit, and delete operations.
                    Use responsibly for testing and validation.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmRolePreview}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-role-preview"
            >
              <Eye className="h-4 w-4 mr-2" />
              Start Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RoleSelector;
